import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from '@mui/material';
import { useTranslation } from '../../../i18n';

interface RenameFolderDialogProps {
  open: boolean;
  currentName: string;
  onClose: () => void;
  onConfirm: (newName: string) => void;
}

const RenameFolderDialog: React.FC<RenameFolderDialogProps> = ({
  open,
  currentName,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [newName, setNewName] = useState<string>(currentName);
  const [validationError, setValidationError] = useState<string>('');

  // Reset name when dialog opens with a new folder
  useEffect(() => {
    if (open) {
      setNewName(currentName);
      setValidationError('');
    }
  }, [open, currentName]);

  // Validate folder name in real-time
  useEffect(() => {
    const trimmedName = newName.trim();

    if (!trimmedName) {
      setValidationError('');
      return;
    }

    if (trimmedName.includes('..')) {
      setValidationError(t('fileManagement.folderNameInvalidDots'));
      return;
    }

    if (trimmedName.startsWith('/') || trimmedName.startsWith('\\')) {
      setValidationError(t('fileManagement.folderNameInvalidStart'));
      return;
    }

    if (trimmedName.includes('/')) {
      setValidationError(t('fileManagement.folderNameInvalidSlash'));
      return;
    }

    setValidationError('');
  }, [newName, t]);

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (
      event.key === 'Enter' &&
      !validationError &&
      newName.trim() &&
      newName.trim() !== currentName
    ) {
      onConfirm(newName.trim());
    }
  };

  const isValid = !validationError && newName.trim() && newName.trim() !== currentName;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('fileManagement.renameFolderTitle')}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label={t('fileManagement.newFolderName')}
          fullWidth
          variant="outlined"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyPress={handleKeyPress}
          error={!!validationError}
          helperText={validationError}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button onClick={() => onConfirm(newName.trim())} variant="contained" disabled={!isValid}>
          {t('fileManagement.rename')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RenameFolderDialog;
